import { clsx } from 'clsx'
import type { Message } from '../../stores/chatStore'
import { useChatStore } from '../../stores/chatStore'

interface ReplyPreviewProps {
  message: Message
  therapistName?: string
}

/**
 * Reply preview bar shown above ChatInput when replying to a message.
 * Shows quoted text with cancel button.
 */
export default function ReplyPreview({ message, therapistName }: ReplyPreviewProps) {
  const setReplyingTo = useChatStore((state) => state.setReplyingTo)
  const isUserMessage = message.role === 'user'

  // Truncate long content
  const displayText = message.content.length > 100
    ? message.content.slice(0, 100) + '...'
    : message.content

  const handleCancel = () => {
    setReplyingTo(null)
  }

  return (
    <div
      data-testid="reply-preview"
      className={clsx(
        'flex items-center gap-2 px-4 py-2 border-l-4',
        isUserMessage
          ? 'bg-primary-50 border-primary-400'
          : 'bg-neutral-50 border-neutral-400'
      )}
    >
      {/* Reply icon */}
      <svg
        className="w-5 h-5 text-neutral-400 flex-shrink-0"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6"
        />
      </svg>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className={clsx(
          'text-xs font-medium',
          isUserMessage ? 'text-primary-600' : 'text-neutral-600'
        )}>
          {isUserMessage ? 'Вы' : therapistName || 'Терапевт'}
        </div>
        <div className="text-sm text-neutral-500 truncate">
          {displayText}
        </div>
      </div>

      {/* Cancel button */}
      <button
        data-testid="reply-cancel"
        onClick={handleCancel}
        className="p-1 hover:bg-neutral-200 rounded-full transition-colors"
        aria-label="Cancel reply"
      >
        <svg
          className="w-5 h-5 text-neutral-400"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </div>
  )
}
